import React, { useMemo, useState } from 'react'
import { PencilIcon, PlusIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { marketingNotasService } from '../../services/marketingNotas'
import type { MarketingEvent } from '../../types/marketingNotas'
import EstoqueModal, { EstoqueModalActions } from '../Estoque/EstoqueModal'
import EstoquePagination from '../Estoque/EstoquePagination'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  PAGE_SIZE,
  PERM_LOADING,
  SELECT_CLASS,
  TEXTAREA_CLASS,
  paginateRows,
  sortRows,
  statusBadgeClass,
} from '../Estoque/estoqueUi'
import { PARAMS_MODULE_NAME, formatDateOnlyPtBr } from './notasUi'

type Props = {
  events: MarketingEvent[]
  loading: boolean
  onChanged: () => void
  canInsert: boolean
  canUpdate: boolean
  canDelete: boolean
}

const BR_UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
]

const emptyForm = {
  name: '',
  description: '',
  event_date: '',
  location: '',
  city: '',
  state: '',
  is_active: true,
}

function dateOnly(value: string | null | undefined): string {
  return (value || '').slice(0, 10)
}

const MarketingEventosTab: React.FC<Props> = ({
  events,
  loading,
  onChanged,
  canInsert,
  canUpdate,
  canDelete,
}) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const [sortKey, setSortKey] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [stateFilter, setStateFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingEvent | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasActiveFilters = Boolean(
    search.trim() || statusFilter !== 'all' || stateFilter || dateFrom || dateTo
  )

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return events.filter((row) => {
      if (statusFilter === 'active' && !row.is_active) return false
      if (statusFilter === 'inactive' && row.is_active) return false
      if (stateFilter && (row.state || '') !== stateFilter) return false
      const eventDate = dateOnly(row.event_date)
      if (dateFrom && (!eventDate || eventDate < dateFrom)) return false
      if (dateTo && (!eventDate || eventDate > dateTo)) return false
      if (!term) return true
      const nameMatch = (row.name || '').toLowerCase().includes(term)
      const descriptionMatch = (row.description || '').toLowerCase().includes(term)
      const locationMatch = (row.location || '').toLowerCase().includes(term)
      const cityMatch = (row.city || '').toLowerCase().includes(term)
      return nameMatch || descriptionMatch || locationMatch || cityMatch
    })
  }, [events, search, statusFilter, stateFilter, dateFrom, dateTo])

  const sorted = useMemo(
    () =>
      sortRows(filtered, sortKey, sortOrder, (row, key) => {
        if (key === 'name') return row.name
        if (key === 'event_date') return row.event_date
        if (key === 'location') return row.location
        if (key === 'city') return row.city
        if (key === 'state') return row.state
        if (key === 'active') return row.is_active ? 1 : 0
        return ''
      }),
    [filtered, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page, PAGE_SIZE)

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('all')
    setStateFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setError(null)
    setModalOpen(true)
  }

  const openEdit = (row: MarketingEvent) => {
    setEditing(row)
    setForm({
      name: row.name,
      description: row.description || '',
      event_date: row.event_date?.slice(0, 10) || '',
      location: row.location || '',
      city: row.city || '',
      state: row.state || '',
      is_active: row.is_active,
    })
    setError(null)
    setModalOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Nome é obrigatório.')
      return
    }
    if (form.state && !BR_UFS.includes(form.state)) {
      setError('Selecione uma UF válida.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        const updated = await marketingNotasService.updateEvent(editing.id, form)
        void logUpdate(
          'marketing_event',
          updated.id,
          `Atualizou evento de marketing: ${updated.name}`,
          {
            name: editing.name,
            event_date: editing.event_date,
            location: editing.location,
            city: editing.city,
            state: editing.state,
            is_active: editing.is_active,
          },
          {
            name: updated.name,
            event_date: updated.event_date,
            location: updated.location,
            city: updated.city,
            state: updated.state,
            is_active: updated.is_active,
          }
        )
      } else {
        const created = await marketingNotasService.createEvent(form)
        void logCreate(
          'marketing_event',
          created.id,
          `Criou evento de marketing: ${created.name}`,
          {
            name: created.name,
            event_date: created.event_date,
            location: created.location,
            city: created.city,
            state: created.state,
            is_active: created.is_active,
          }
        )
      }
      setModalOpen(false)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar evento.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: MarketingEvent) => {
    if (!window.confirm(`Excluir o evento "${row.name}"?`)) return
    try {
      await marketingNotasService.softDeleteEvent(row.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pelo usuário',
      })
      void logDelete('marketing_event', row.id, `Excluiu evento de marketing: ${row.name}`, {
        name: row.name,
      })
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir evento.')
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
            Eventos
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Cadastro de eventos para vínculo nas notas fiscais.
          </Text>
        </div>
        {canInsert && (
          <PermissionGuard
            moduleName={PARAMS_MODULE_NAME}
            permissionType="insert"
            loadingComponent={PERM_LOADING}
          >
            <Button
              type="button"
              variant="solid"
              onClick={openCreate}
              className="inline-flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Novo evento
            </Button>
          </PermissionGuard>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Buscar
          </label>
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="Nome, local ou cidade"
            className="min-w-[180px]"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="all">Todos</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            UF
          </label>
          <select
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value)
              setPage(1)
            }}
            className={SELECT_CLASS}
          >
            <option value="">Todas</option>
            {BR_UFS.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Data de
          </label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Data até
          </label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={clearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center gap-1 whitespace-nowrap"
          >
            <XCircleIcon className="h-4 w-4" />
            Limpar filtros
          </Button>
        </div>
      </div>

      {loading && events.length === 0 ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <SortableTableHeader
                    sortKey="name"
                    currentSort={sortKey}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                  >
                    Nome
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="event_date"
                    currentSort={sortKey}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                  >
                    Data
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="location"
                    currentSort={sortKey}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                  >
                    Local
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="city"
                    currentSort={sortKey}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                  >
                    Cidade
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="state"
                    currentSort={sortKey}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                  >
                    UF
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="active"
                    currentSort={sortKey}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                  >
                    Status
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      {hasActiveFilters
                        ? 'Nenhum evento encontrado com os filtros atuais.'
                        : 'Nenhum evento cadastrado.'}
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {row.name}
                        {row.description ? (
                          <div className="mt-0.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                            {row.description}
                          </div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {formatDateOnlyPtBr(row.event_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {row.location || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {row.city || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                        {row.state || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(row.is_active)}`}
                        >
                          {row.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {canUpdate && (
                            <PermissionGuard
                              moduleName={PARAMS_MODULE_NAME}
                              permissionType="update"
                              loadingComponent={PERM_LOADING}
                            >
                              <button
                                type="button"
                                className={ICON_BTN_CLASS}
                                onClick={() => openEdit(row)}
                                title="Editar"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                          {canDelete && (
                            <PermissionGuard
                              moduleName={PARAMS_MODULE_NAME}
                              permissionType="delete"
                              loadingComponent={PERM_LOADING}
                            >
                              <button
                                type="button"
                                className={ICON_BTN_DANGER_CLASS}
                                onClick={() => void handleDelete(row)}
                                title="Excluir"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-4">
            <EstoquePagination
              page={page}
              total={sorted.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      {modalOpen && (
        <EstoqueModal
          title={editing ? 'Editar evento' : 'Novo evento'}
          onClose={() => !saving && setModalOpen(false)}
          busy={saving}
          onSubmit={handleSave}
          footer={
            <EstoqueModalActions
              onCancel={() => setModalOpen(false)}
              saving={saving}
              submitLabel={editing ? 'Salvar' : 'Criar'}
            />
          }
        >
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nome *
            </label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Descrição
            </label>
            <textarea
              rows={2}
              className={TEXTAREA_CLASS}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Data
              </label>
              <Input
                type="date"
                value={form.event_date}
                onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Local
              </label>
              <Input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="Nome do espaço / venue"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Cidade
              </label>
              <Input
                value={form.city}
                onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Estado (UF)
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              >
                <option value="">—</option>
                {BR_UFS.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Status
            </label>
            <select
              className={SELECT_CLASS + ' w-full'}
              value={form.is_active ? '1' : '0'}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === '1' }))}
            >
              <option value="1">Ativo</option>
              <option value="0">Inativo</option>
            </select>
          </div>
        </EstoqueModal>
      )}
    </div>
  )
}

export default MarketingEventosTab
